/**
 * Campaign Adapter Utilities
 *
 * Maps between backend API types and frontend UI types.
 * The backend uses a different naming convention and status enum than the UI expects.
 */

import {
  Campaign,
  CampaignDetail,
  CampaignRecipient as APICampaignRecipient,
  CampaignStats,
  CampaignRecipientStatus,
} from '../types/campaign';

// UI types used by detail page components
export interface UICampaignRecipient {
  id: string;
  email: string;
  name: string;
  company: string;
  title: string;
  status: 'draft' | 'queued' | 'clicked' | 'opened' | 'delivered' | 'sent' | 'bounced' | 'replied';
  clickCount: number;
  sentAt?: string;
  openedAt?: string;
  clickedAt?: string;
  linkedinUrl?: string;
  companyDomain?: string;
  phone?: string;
  leadTableId?: string;
  metadata?: Record<string, unknown>;
  activity: ActivityEvent[];
}

export interface ActivityEvent {
  type: 'sent' | 'delivered' | 'opened' | 'clicked' | 'replied' | 'bounced';
  timestamp: string;
  details?: string;
}

export interface UICampaignDataset {
  id: string;
  name: string;
  recipientCount?: number;
}

export interface UICampaignData {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'draft' | 'completed';
  senderEmail: string;
  datasets: UICampaignDataset[];
  subject: string;
  body: string;
  emailTemplateId?: string;
  credentialId?: string;
  sendWindow: { start: string; end: string; days: string[] };
  stats: {
    draft: number;
    queued: number;
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
    bounced: number;
  };
  recipients: UICampaignRecipient[];
}

/**
 * Map API recipient status to UI recipient status
 *
 * Note: The mapping depends on context:
 * - In draft campaigns: enrolled recipients are just "enrolled" (pending)
 * - In active/paused campaigns: enrolled means queued for sending
 * - active/completed means sent
 */
export function mapRecipientStatusToUI(
  apiStatus: CampaignRecipientStatus,
  campaignStatus?: Campaign['status']
): UICampaignRecipient['status'] {
  // For draft campaigns, keep enrolled recipients as "queued" but show them as pending
  // The UI should interpret this based on campaign status
  const statusMap: Record<CampaignRecipientStatus, UICampaignRecipient['status']> = {
    enrolled: 'queued',  // Will be shown as "Enrolled" in draft mode by UI
    active: 'queued',    // In the sending queue
    paused: 'queued',
    completed: 'sent',
    unsubscribed: 'sent', // Was sent to, then opted out
    bounced: 'bounced',
    removed: 'queued',   // Removed recipients are filtered by backend; fallback only
  };

  // For draft campaigns, enrolled recipients haven't been queued yet
  if (campaignStatus === 'draft' && apiStatus === 'enrolled') {
    return 'draft';
  }

  return statusMap[apiStatus] || 'queued';
}

/**
 * Map API campaign status to UI campaign status
 */
export function mapCampaignStatusToUI(
  apiStatus: Campaign['status']
): UICampaignData['status'] {
  const statusMap: Record<Campaign['status'], UICampaignData['status']> = {
    draft: 'draft',
    active: 'active',
    paused: 'paused',
    completed: 'completed',
    archived: 'completed',
  };
  return statusMap[apiStatus] || 'draft';
}

/**
 * Map UI campaign status to API campaign status
 */
export function mapCampaignStatusToAPI(
  uiStatus: UICampaignData['status']
): Campaign['status'] {
  const statusMap: Record<UICampaignData['status'], Campaign['status']> = {
    draft: 'draft',
    active: 'active',
    paused: 'paused',
    completed: 'completed',
  };
  return statusMap[uiStatus] || 'draft';
}

/**
 * Convert API recipient to UI recipient format
 */
export function adaptRecipientToUI(
  apiRecipient: APICampaignRecipient,
  campaignStatus?: Campaign['status']
): UICampaignRecipient {
  // Extract domain from email
  const emailParts = apiRecipient.email?.split('@');
  const companyDomain = emailParts?.[1] || undefined;

  return {
    id: apiRecipient.id,
    email: apiRecipient.email || '',
    name: apiRecipient.name || 'Unknown',
    company: apiRecipient.company || '',
    title: apiRecipient.title || '',
    status: mapRecipientStatusToUI(apiRecipient.status, campaignStatus),
    clickCount: 0, // Would come from entity_activities
    sentAt: apiRecipient.enrolled_at,
    linkedinUrl: apiRecipient.linkedin_url,
    companyDomain,
    phone: apiRecipient.phone || undefined,
    leadTableId: apiRecipient.lead_table_id || undefined,
    metadata: apiRecipient.metadata || undefined,
    activity: [], // Would be populated from entity_activities
  };
}

/**
 * Convert API campaign stats to UI stats format.
 *
 * Uses recipient_status breakdown for draft/queued/bounced counts,
 * and activity counts for sent/opened/clicked/replied.
 *
 * Pipeline: Draft (enrolled, campaign not active) -> Queued (active/enrolled in active campaign)
 *           -> Sent -> Opened -> Clicked -> Replied
 *           Bounced shown separately.
 */
export function adaptStatsToUI(
  stats: CampaignStats | null | undefined,
  recipientCount: number,
  campaignStatus?: Campaign['status']
): UICampaignData['stats'] {
  if (!stats) {
    return {
      draft: campaignStatus === 'draft' ? recipientCount : 0,
      queued: campaignStatus === 'draft' ? 0 : recipientCount,
      sent: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
      bounced: 0,
    };
  }

  const rs = stats.recipient_status || {};
  const enrolled = rs.enrolled || 0;
  const active = rs.active || 0;
  const bounced = rs.bounced || 0;

  // Draft: enrolled recipients when campaign hasn't been activated yet
  // Queued: enrolled + active recipients in an active/paused campaign (not yet sent)
  const isDraft = campaignStatus === 'draft';
  const draft = isDraft ? enrolled + active : 0;
  const queued = isDraft ? 0 : Math.max(0, stats.total_recipients - stats.emails_sent - bounced);

  return {
    draft,
    queued,
    sent: stats.emails_sent,
    opened: stats.emails_opened,
    clicked: stats.emails_clicked,
    replied: stats.emails_replied,
    bounced: bounced > 0 ? bounced : stats.emails_bounced,
  };
}

/**
 * Convert API campaign to UI campaign format
 */
export function adaptCampaignToUI(
  campaign: CampaignDetail,
  recipients: APICampaignRecipient[] = [],
  senderEmail: string = 'user@company.com'
): UICampaignData {
  // Map datasets
  const datasets: UICampaignDataset[] = (campaign.datasets || []).map(d => ({
    id: d.lead_table_id,
    name: d.lead_table_name || 'Dataset',
    recipientCount: d.lead_count,
  }));

  // Map recipients (pass campaign status for context-aware status mapping)
  const uiRecipients = recipients.map(r => adaptRecipientToUI(r, campaign.status));

  // Map stats (pass campaign status for draft vs queued distinction)
  const stats = adaptStatsToUI(campaign.stats, campaign.recipients_count, campaign.status);

  return {
    id: campaign.id,
    name: campaign.name,
    status: mapCampaignStatusToUI(campaign.status),
    senderEmail,
    datasets,
    // Email content from campaign (template takes precedence over custom content)
    subject: campaign.email_subject || '',
    body: campaign.email_body || '',
    emailTemplateId: campaign.email_template_id,
    credentialId: campaign.credential_id,
    sendWindow: {
      start: campaign.send_window_start || '9:00 AM',
      end: campaign.send_window_end || '5:00 PM',
      days: campaign.send_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    },
    stats,
    recipients: uiRecipients,
  };
}

/**
 * Convert UI campaign data back to API update format
 */
export function adaptCampaignToAPI(
  uiCampaign: Partial<UICampaignData>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (uiCampaign.name !== undefined) {
    result.name = uiCampaign.name;
  }

  if (uiCampaign.status !== undefined) {
    result.status = mapCampaignStatusToAPI(uiCampaign.status);
  }

  if (uiCampaign.subject !== undefined) {
    result.email_subject = uiCampaign.subject;
  }

  if (uiCampaign.body !== undefined) {
    result.email_body = uiCampaign.body;
  }

  if (uiCampaign.emailTemplateId !== undefined) {
    result.email_template_id = uiCampaign.emailTemplateId;
  }

  if (uiCampaign.credentialId !== undefined) {
    result.credential_id = uiCampaign.credentialId;
  }

  if (uiCampaign.sendWindow !== undefined) {
    result.send_window_start = uiCampaign.sendWindow.start;
    result.send_window_end = uiCampaign.sendWindow.end;
    result.send_days = uiCampaign.sendWindow.days;
  }

  return result;
}

/**
 * Sidebar campaign format for quick display
 */
export interface SidebarCampaign {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'draft' | 'completed';
  recipients_count: number;
  created_at: string;
}

/**
 * Convert API campaign to sidebar display format
 */
export function adaptCampaignToSidebar(campaign: Campaign): SidebarCampaign {
  return {
    id: campaign.id,
    name: campaign.name,
    status: campaign.status === 'archived' ? 'completed' : campaign.status,
    recipients_count: campaign.recipients_count,
    created_at: campaign.created_at,
  };
}
